import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './lib/AuthContext';
import { AlertProvider } from './components/AlertSystem';
import { ProtectedRoute, GuestRoute, PublicRoute } from './components/RouteProtection';
import AdminRoute from './components/AdminRoute';
import Navigation from './components/Navigation';
import ScrollToTop from './components/ScrollToTop';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import PricingSection from './components/PricingSection';
import LeadCaptureForm from './components/LeadCaptureForm';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import DashboardOverview from './components/DashboardOverview';
import DashboardAlerts from './components/DashboardAlerts';
import DashboardReports from './components/DashboardReports';
import DashboardBlogs from './components/DashboardBlogs';
import DashboardBlogPost from './components/DashboardBlogPost';
import DashboardProfile from './components/DashboardProfile';
import DashboardAIInsights from './components/DashboardAIInsights';
import DashboardCustomMonitoring from './components/DashboardCustomMonitoring';
import AdminUsersManagement from './components/AdminUsersManagement';
import AdminRoleManagement from './components/AdminRoleManagement';
import AdminLeadsManagement from './components/AdminLeadsManagement';
import AdminAlertsManagement from './components/AdminAlertsManagement';
import AdminReportsManagement from './components/AdminReportsManagement';
import AdminBlogsManagement from './components/AdminBlogsManagement';
import AdminCustomMonitoringManagement from './components/AdminCustomMonitoringManagement';
import BlogPage from './components/BlogPage';
import './App.css';

// Landing Page Component
const LandingPage: React.FC = () => {
  return (
    <>
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <PricingSection />
      <LeadCaptureForm />
      <ContactSection />
    </>
  );
};

const DashboardPage: React.FC = () => {
  return <DashboardOverview />;
};

// Dashboard Alerts Page
const DashboardAlertsPage: React.FC = () => {
  return <DashboardAlerts />;
};

// Dashboard Reports Page
const DashboardReportsPage: React.FC = () => {
  return <DashboardReports />;
};

// Dashboard Blogs Page
const DashboardBlogsPage: React.FC = () => {
  return <DashboardBlogs />;
};

// Dashboard Profile Page
const DashboardProfilePage: React.FC = () => {
  return <DashboardProfile />;
};

// Dashboard AI Insights Page
const DashboardAIInsightsPage: React.FC = () => {
  return <DashboardAIInsights />;
};

// Dashboard Custom Monitoring Page
const DashboardCustomMonitoringPage: React.FC = () => {
  return <DashboardCustomMonitoring />;
};

// 404 Not Found Page
const NotFoundPage: React.FC = () => {
  return (
    <div style={{ padding: '140px 20px 20px', textAlign: 'center', minHeight: '80vh' }}>
      <h1>404 - Page Not Found</h1>
      <p>The page you're looking for doesn't exist.</p>
      <a href="/" style={{ color: '#6366f1', textDecoration: 'none' }}>Go back to Home</a>
    </div>
  );
};

// Layout wrapper component
const LayoutWrapper: React.FC = () => {
  const location = useLocation();
  
  // Check if current path is a dashboard or admin page
  const isDashboardPage = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/admin');
  
  return (
    <>
      {!isDashboardPage && <Navigation />}
      <main>
        <Routes>
          {/* Public Pages - Always accessible */}
          <Route path="/" element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          } />
          <Route path="/privacy-policy" element={
            <PublicRoute>
              <PrivacyPolicy />
            </PublicRoute>
          } />
          <Route path="/terms-of-service" element={
            <PublicRoute>
              <TermsOfService />
            </PublicRoute>
          } />
          <Route path="/blog/:id" element={
            <PublicRoute>
              <BlogPage />
            </PublicRoute>
          } />
          
          {/* Authentication Pages - Only for non-authenticated users */}
          <Route path="/login" element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          } />
          <Route path="/register" element={
            <GuestRoute>
              <RegisterPage />
            </GuestRoute>
          } />
          <Route path="/forgot-password" element={
            <GuestRoute>
              <ForgotPasswordPage />
            </GuestRoute>
          } />
          <Route path="/reset-password" element={
            <GuestRoute>
              <ResetPasswordPage />
            </GuestRoute>
          } />
          
          {/* Dashboard Pages - Require authentication */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/alerts" element={
            <ProtectedRoute>
              <DashboardAlertsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/reports" element={
            <ProtectedRoute>
              <DashboardReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/blogs" element={
            <ProtectedRoute>
              <DashboardBlogsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/blogs/:id" element={
            <ProtectedRoute>
              <DashboardBlogPost />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/profile" element={
            <ProtectedRoute>
              <DashboardProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/ai-insights" element={
            <ProtectedRoute>
              <DashboardAIInsightsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/custom-monitoring" element={
            <ProtectedRoute>
              <DashboardCustomMonitoringPage />
            </ProtectedRoute>
          } />
          
          {/* Admin Pages - Require authentication + specific permissions */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminUsersManagement />
            </AdminRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute requiredPermission="users_view">
              <AdminUsersManagement />
            </AdminRoute>
          } />
          <Route path="/admin/roles" element={
            <AdminRoute requiredPermission="roles_view">
              <AdminRoleManagement />
            </AdminRoute>
          } />
          <Route path="/admin/leads" element={
            <AdminRoute requiredPermission="leads_view">
              <AdminLeadsManagement />
            </AdminRoute>
          } />
          <Route path="/admin/alerts" element={
            <AdminRoute requiredPermission="alerts_view">
              <AdminAlertsManagement />
            </AdminRoute>
          } />
          <Route path="/admin/reports" element={
            <AdminRoute requiredPermission="reports_view">
              <AdminReportsManagement />
            </AdminRoute>
          } />
          <Route path="/admin/blogs" element={
            <AdminRoute requiredPermission="blogs_view">
              <AdminBlogsManagement />
            </AdminRoute>
          } />
          <Route path="/admin/custom-monitoring" element={
            <AdminRoute requiredPermission="custom_monitoring_view">
              <AdminCustomMonitoringManagement />
            </AdminRoute>
          } />
          
          {/* 404 Page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {!isDashboardPage && <Footer />}
    </>
  );
};

function App() {
  return (
    <AlertProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <div className="App">
            <LayoutWrapper />
          </div>
        </Router>
      </AuthProvider>
    </AlertProvider>
  );
}

export default App;
